import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  input,
  InputSignal,
  OutputEmitterRef,
  output,
} from "@angular/core";
import _isEqual from "lodash.isequal";
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  Subscription,
  take,
} from "rxjs";
import { VideoService } from "./video.service";
import { QbService } from "../qb.service";
import { ModalService } from "../modal.service";
import { CallfeebackComponent } from "../callfeeback/callfeeback.component";
import { MatDialog } from "@angular/material/dialog";
import { SharedService } from "src/app/shared/shared.service";
import { TranslateService } from "@ngx-translate/core";
import { UserIdleService } from "angular-user-idle";
import { CallStateService } from "src/app/shared/call-state/call-state.service";

declare var QB: any;

@Component({
  selector: "app-video",
  templateUrl: "./video.component.html",
  styleUrls: ["./video.component.scss"],
})
export class VideoComponent implements OnInit, OnDestroy {
  // Inputs
  userData: InputSignal<any> = input();
  consultId: InputSignal<string> = input();
  consultType: InputSignal<number> = input();
  // Outputs
  startVideoCall: OutputEmitterRef<void> = output();

  displayCamera = false;
  qbSessionSubscription: Subscription;
  onStopCallSubscription: Subscription;
  onSessionCloseSubscription: Subscription;
  onRejectCallSubscription: Subscription;
  onAcceptCallSubscription: Subscription;
  onUserNotAnswerSubscription: Subscription;
  onRemoteStreamSubscription: Subscription;
  QBSession: any;
  qbConnectionSubscriptions: Subscription[] = [];
  muteStream: boolean = false;
  muteSound: boolean = false;
  fullScreenDisplay: boolean = false;
  hours: string;
  minutes: string;
  seconds: string;
  currentTime: number;
  connected: boolean = false;
  callAccepted: boolean = false;
  mediaParams = {
    audio: true,
    video: false,
    options: {
      muted: true,
      mirror: false,
    },
    elemId: "localVideo",
  };
  timerResult: any;
  extension = {};
  rotationAngle = 0;
  private localStream: MediaStream;
  private subscriptions: Subscription = new Subscription();
  dialogRef: any = null;

  constructor(
    private videoService: VideoService,
    private qbService: QbService,
    private modalService: ModalService,
    private sharedService: SharedService,
    public dialog: MatDialog,
    public translationService: TranslateService,
    private userIdle: UserIdleService,
    private callStateService: CallStateService,
  ) {}

  async ngOnInit(): Promise<void> {
    await navigator.mediaDevices.enumerateDevices().then((devices) => {
      if (devices.find((device) => device.kind === "videoinput")) {
        this.displayCamera = true;
      }
    });
    this.configureSettings();
    this.makeCall();

    this._addEventListenerUnload();
  }

  ngOnDestroy() {
    this._stopQBSession();

    this.qbConnectionSubscriptions.forEach((subscription) =>
      subscription.unsubscribe(),
    );

    if (this.qbSessionSubscription) {
      this.qbSessionSubscription.unsubscribe();
    }
  }

  emitStartVideoCall() {
    this.startVideoCall.emit();
  }

  rotateVideo() {
    this.rotationAngle = (this.rotationAngle + 90) % 360;
    const videoElement = document.getElementById(
      "remoteVideo",
    ) as HTMLVideoElement;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (videoElement) {
      videoElement.style.transform = `rotate(${this.rotationAngle}deg)`;
    }
  }

  // configuration settings for all the events that takes place for the whole session
  configureSettings() {
    this.qbSessionSubscription = this.videoService.sessionObservable.subscribe(
      (res) => {
        console.log("session", res);
        this.QBSession = res;
        if (Object.keys(res).length > 0) {
          this.getUserMedia();
        }
      },
    );

    // subscribing to all the listener events
    this.onRejectCallSubscription = this.qbService.rejectCallObservable
      .pipe(
        filter((data) => {
          console.log("rejectCallObservable");
          console.log(data);

          if (this.callAccepted) {
            return false;
          }

          return true;
        }),
        debounceTime(300),
        distinctUntilChanged(_isEqual),
        take(1),
      )
      .subscribe((data) => {
        this.translationService
          .get("User rejected the call")
          .subscribe((text: string) => {
            this.sharedService.showErrorMessage(text, "single");
          });
      });

    this.subscriptions.add(
      this.qbService.stopCallObservable.subscribe((data) => {
        this.translationService
          .get("User disconnected the call")
          .subscribe((text: string) => {
            this.sharedService.showErrorMessage(text, "single");
          });
      }),
    );

    this.subscriptions.add(
      this.qbService.sessionCloseObservable
        .pipe(
          filter(() => {
            return !!this.QBSession;
          }),
          debounceTime(300),
          distinctUntilChanged(_isEqual),
          take(1),
        )
        .subscribe((data) => {
          console.log("sessionCloseObservable");
          console.log(data);
          this.videoService.stopRecording(true);
          this.userIdle.startWatching();
          this.localStream = null;
          this.closeFullscreen();
          this.callFeedbackPopup();
          this.modalService.disconnectCallSubject.next({
            callStatus: "disconnected",
          });

          this.QBSession = null;
        }),
    );

    this.subscriptions.add(
      this.qbService.acceptCallObservable.subscribe((data) => {}),
    );

    this.subscriptions.add(
      this.qbService.remoteStreamObservable.subscribe((data) => {
        if (!this.QBSession || !this.localStream) {
          return;
        }
        const remoteStream: MediaStream = data.stream;

        this.QBSession.attachMediaStream("remoteVideo", remoteStream);
        this.connected = true;
        this.callAccepted = true;
        this.modalService.callStatus({
          callStarted: true,
        });
        this.videoService.startRecording(
          this.consultId(),
          this.localStream,
          remoteStream,
        );
        this.userIdle.stopWatching();
      }),
    );

    this.subscriptions.add(
      this.qbService.userNotAnswerObservable.subscribe((data) => {
        this.translationService
          .get("User not answered the call")
          .subscribe((text: string) => {
            this.sharedService.showErrorMessage(text, "single");
          });
      }),
    );
  }

  callFeedbackPopup() {
    console.log("callFeedbackPopup");
    if (this.dialogRef) {
      this.dialogRef.close();
    }

    this.dialogRef = this.dialog.open(CallfeebackComponent, {
      width: "418px",
      disableClose: true,
      autoFocus: false,
      closeOnNavigation: true,
      data: this.consultType(),
    });

    this.dialogRef.afterClosed().subscribe(() => {
      this.dialogRef = null;
    });
  }

  // initiating  call
  makeCall() {
    let qbUserId;
    if (this.userData().patient.parent) {
      qbUserId = this.userData().patient.parent.quickbloxId;
    } else {
      qbUserId = this.userData().patient.quickbloxId;
    }

    if (!qbUserId) {
      this.translationService
        .get("Error con el usuario")
        .subscribe((text: string) => {
          this.sharedService.showErrorMessage(text, "single");
        });
      this.displayCamera = false;
      this.callStateService.emitError(true);
      return;
    }

    var calleeId = [qbUserId];
    var sessionType = QB.webrtc.CallType.VIDEO; // AUDIO is also possible
    var callerID = null; // Your user ID (optional, will be defined from chat connection)
    var additionalOptions = { bandwith: 512 }; // The video bandwith (number, optional, 0 = unlimited)
    this.videoService.createSession(
      calleeId,
      sessionType,
      callerID,
      additionalOptions,
    );
  }

  // Get local media stream
  getUserMedia() {
    const params = this.getMediaParams();
    this.QBSession.getUserMedia(params, (err: any, stream: any) => {
      if (stream?.active) {
        this.localStream = stream;
        this.makeCallRequest();
      }
    });
  }

  // get User media params

  getMediaParams() {
    return { ...this.mediaParams, video: this.displayCamera };
  }

  // Making call request
  makeCallRequest() {
    this.QBSession.call(this.extension, (error: any) => {
      console.log("call response", error);

      if (!error) {
        this.emitStartVideoCall();
      }
    });
  }

  disConnect(): void {
    var extension = {};
    this.QBSession.stop(extension);
  }

  muteVideo() {
    this.QBSession.mute("video");
    this.muteStream = true;
  }

  muteMike() {
    this.QBSession.mute("audio");
    this.muteSound = true;
  }

  unMuteVideo() {
    this.QBSession.unmute("video");
    this.muteStream = false;
  }

  unMuteMike() {
    this.QBSession.unmute("audio");
    this.muteSound = false;
  }

  openFullscreen() {
    var elem = document.getElementById("video-container");
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
      this.fullScreenDisplay = true;
    }
  }

  setCurrentTime(data) {
    this.currentTime = Math.floor(data.target.currentTime);

    var hrs = Math.floor(this.currentTime / 3600);
    var min = Math.floor((this.currentTime - hrs * 3600) / 60);
    var seconds = this.currentTime - hrs * 3600 - min * 60;
    seconds = Math.round(seconds * 100) / 100;

    this.timerResult = hrs < 10 ? "0" + hrs : hrs;
    this.timerResult += ":" + (min < 10 ? "0" + min : min);
    this.timerResult += ":" + (seconds < 10 ? "0" + seconds : seconds);
  }

  pad(val) {
    var valString = val + "";
    if (valString.length < 2) {
      return "0" + valString;
    } else {
      return valString;
    }
  }

  closeFullscreen() {
    this.fullScreenDisplay = false;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
  }

  handleError(error) {
    let errorMessage = "";
    if (error.error instanceof ErrorEvent) {
      // client-side error
      errorMessage = error.error.message;
      this.sharedService.showErrorMessage(errorMessage, "single");
    } else {
      // server-side error

      if (error.status == 422) {
        let values = [];
        for (let key in error.error.messages) {
          values.push(error.error.messages[key]);
        }
        this.sharedService.showMultipleErrors(values, "multi");
      } else {
        if (typeof error.error == "object") {
          errorMessage = error.error.messages[0];
        } else {
          errorMessage = error.error;
        }
        this.sharedService.showErrorMessage(errorMessage, "single");
      }
    }
  }

  private _addEventListenerUnload() {
    window.addEventListener("unload", () => {
      this._stopQBSession();
    });
  }

  private _stopQBSession() {
    if (this.QBSession) {
      this.QBSession.stop({});
    }
  }
}
